/**
 * Convert DRBFM Failure Modes to Verification Items Dialog
 *
 * 3-step wizard dialog:
 * 1. Select failure modes to convert
 * 2. Choose target verification session/unit
 * 3. Preview generated items and confirm
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import {
  PlaylistAdd as ConvertIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import {
  FailureMode,
  DesignChange,
  Countermeasure,
  getSeverityColor,
  ConvertToVerificationRequest,
} from '../../types';
import { VerificationSession } from '../../types/verification';
import { convertToVerification } from '../../services/drbfm.service';
import { listSessions, getSession } from '../../services/verificationService';

// ==============================================================================
// Mapping Preview Helpers (mirrors backend logic for preview)
// ==============================================================================

const IMPACT_TO_CLASSIFICATION: Record<string, string[]> = {
  Safety: ['safety'],
  Quality: ['performance', 'reliability'],
  Manufacturing: ['function', 'cost'],
  Design: ['performance', 'function'],
  Cost: ['cost'],
  Maintenance: ['maintenance'],
};

const SEVERITY_TO_METHODS: Record<string, string[]> = {
  critical: ['analysis', 'pre_test', 'actual_confirm', 'dr1'],
  high: ['design_calc', 'analysis', 'actual_confirm'],
  medium: ['design_calc', 'dr1'],
  low: ['dr1', 'knowhow'],
};

interface PreviewItem {
  itemName: string;
  specification: string;
  classifications: string[];
  standardTarget: string;
  methods: string[];
  severity: string;
}

function generatePreviewItems(
  selectedFMs: FailureMode[],
  designChanges: DesignChange[],
  countermeasures: Countermeasure[],
): PreviewItem[] {
  const dcMap = new Map(designChanges.map((dc) => [dc.id, dc]));
  const cmByFM = new Map<string, Countermeasure[]>();
  for (const cm of countermeasures) {
    if (cm.failureModeId) {
      const list = cmByFM.get(cm.failureModeId) || [];
      list.push(cm);
      cmByFM.set(cm.failureModeId, list);
    }
  }

  return selectedFMs.map((fm) => {
    const dc = dcMap.get(fm.designChangeId || '');
    const fmCMs = (cmByFM.get(fm.id) || []).sort(
      (a, b) => a.priority - b.priority,
    );

    const classifications: string[] = [];
    for (const area of dc?.impactAreas || []) {
      for (const cls of IMPACT_TO_CLASSIFICATION[area] || []) {
        if (!classifications.includes(cls)) classifications.push(cls);
      }
    }
    if (classifications.length === 0) classifications.push('performance');

    const itemName =
      fm.failureDescription.length > 60
        ? fm.failureDescription.slice(0, 57) + '...'
        : fm.failureDescription;

    return {
      itemName,
      specification: dc?.newSpecification || '',
      classifications,
      standardTarget: fmCMs[0]?.description || '',
      methods: SEVERITY_TO_METHODS[fm.severity] || ['dr1'],
      severity: fm.severity,
    };
  });
}

// ==============================================================================
// Component Props
// ==============================================================================

interface ConvertToVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  drbfmSessionId: string;
  drbfmItemName: string;
  failureModes: FailureMode[];
  designChanges: DesignChange[];
  countermeasures: Countermeasure[];
}

const STEP_KEYS = [
  'selectFailureModes',
  'chooseTarget',
  'preview',
] as const;

// ==============================================================================
// Component
// ==============================================================================

const ConvertToVerificationDialog: React.FC<
  ConvertToVerificationDialogProps
> = ({
  open,
  onClose,
  drbfmSessionId,
  drbfmItemName,
  failureModes,
  designChanges,
  countermeasures,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: Failure mode selection
  const [selectedFMIds, setSelectedFMIds] = useState<Set<string>>(new Set());

  // Step 2: Target session/unit
  const [targetMode, setTargetMode] = useState<'existing' | 'new'>('new');
  const [existingSessions, setExistingSessions] = useState<
    VerificationSession[]
  >([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [selectedSession, setSelectedSession] =
    useState<VerificationSession | null>(null);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newModelName, setNewModelName] = useState('');
  const [newUnitNumber, setNewUnitNumber] = useState('01');
  const [newUnitName, setNewUnitName] = useState('');

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resultSessionId, setResultSessionId] = useState<string | null>(null);
  const [resultItemsCount, setResultItemsCount] = useState(0);

  // Sessions loading
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Initialize: select critical/high severity failure modes by default
  useEffect(() => {
    if (open) {
      const defaultSelected = new Set(
        failureModes
          .filter(
            (fm) => fm.severity === 'critical' || fm.severity === 'high',
          )
          .map((fm) => fm.id),
      );
      setSelectedFMIds(defaultSelected);
      setActiveStep(0);
      setError(null);
      setSuccess(false);
      setTargetMode('new');
      setNewSessionTitle(t('drbfm.convert.defaultSessionTitle', { itemName: drbfmItemName }));
      setNewModelName('');
      setNewUnitNumber('01');
      setNewUnitName(drbfmItemName);
      setSelectedSessionId('');
      setSelectedUnitId('');
      setSelectedSession(null);
      setExistingSessions([]);
    }
    // Initialize on dialog open / new DRBFM identity only — NOT on language
    // change. t() supplies the initial localized default title; making it a
    // dependency would reset the in-progress wizard whenever the user toggles
    // language (PR #790 review).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, failureModes, drbfmItemName]);

  // Load existing verification sessions when switching to "existing" mode
  useEffect(() => {
    if (targetMode === 'existing' && existingSessions.length === 0) {
      setSessionsLoading(true);
      listSessions({ limit: 50, offset: 0 })
        .then((res) => {
          setExistingSessions(res.sessions);
        })
        .catch(() => {
          setError(t('drbfm.convert.fetchSessionsFailed'));
        })
        .finally(() => setSessionsLoading(false));
    }
    // t() is only referenced for the failure message; it must not retrigger the
    // fetch on language change (PR #790 review).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMode, existingSessions.length]);

  // Load session details when a session is selected
  useEffect(() => {
    if (selectedSessionId) {
      getSession(selectedSessionId)
        .then((s) => setSelectedSession(s))
        .catch(() => setSelectedSession(null));
    } else {
      setSelectedSession(null);
    }
  }, [selectedSessionId]);

  // Sorted failure modes: critical > high > medium > low
  const sortedFMs = useMemo(() => {
    const order: Record<string, number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
    };
    return [...failureModes].sort(
      (a, b) => (order[a.severity] ?? 4) - (order[b.severity] ?? 4),
    );
  }, [failureModes]);

  // Selected failure modes for preview
  const selectedFMs = useMemo(
    () => sortedFMs.filter((fm) => selectedFMIds.has(fm.id)),
    [sortedFMs, selectedFMIds],
  );

  // Preview items
  const previewItems = useMemo(
    () => generatePreviewItems(selectedFMs, designChanges, countermeasures),
    [selectedFMs, designChanges, countermeasures],
  );

  // Handlers
  const handleToggleFM = useCallback((fmId: string) => {
    setSelectedFMIds((prev) => {
      const next = new Set(prev);
      if (next.has(fmId)) {
        next.delete(fmId);
      } else {
        next.add(fmId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedFMIds(new Set(failureModes.map((fm) => fm.id)));
  }, [failureModes]);

  const handleDeselectAll = useCallback(() => {
    setSelectedFMIds(new Set());
  }, []);

  const canProceedStep1 = selectedFMIds.size > 0;
  const canProceedStep2 =
    targetMode === 'new'
      ? newSessionTitle.trim().length > 0
      : selectedSessionId.length > 0;

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const request: ConvertToVerificationRequest = {
        failureModeIds: Array.from(selectedFMIds),
        verificationSessionId:
          targetMode === 'existing' ? selectedSessionId : null,
        verificationUnitId:
          targetMode === 'existing' && selectedUnitId
            ? selectedUnitId
            : null,
        newSessionTitle: targetMode === 'new' ? newSessionTitle : null,
        newSessionModelName: targetMode === 'new' ? newModelName : null,
        newUnitNumber: targetMode === 'new' ? newUnitNumber : null,
        newUnitName:
          targetMode === 'new' ? newUnitName || drbfmItemName : null,
      };

      const result = await convertToVerification(drbfmSessionId, request);
      setSuccess(true);
      setResultSessionId(result.verificationSessionId);
      setResultItemsCount(result.itemsCreated);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('drbfm.convert.convertFailed'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateToVerification = () => {
    onClose();
    if (resultSessionId) {
      navigate(`/verification?session=${resultSessionId}`);
    }
  };

  const getClassificationLabel = (classification: string) => (
    t(`drbfm.convert.classifications.${classification}`, classification)
  );

  const getMethodLabel = (method: string) => (
    t(`drbfm.convert.methods.${method}`, method)
  );

  // ============================================================================
  // Render Steps
  // ============================================================================

  const renderStep1 = () => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('drbfm.convert.selectFailureModesDescription', {
            selected: selectedFMIds.size,
            total: failureModes.length,
          })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={handleSelectAll}>
            {t('drbfm.convert.selectAll')}
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            {t('drbfm.convert.deselectAll')}
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 400 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{t('drbfm.table.severity')}</TableCell>
              <TableCell>{t('drbfm.table.failureMode')}</TableCell>
              <TableCell>{t('drbfm.table.failureCause')}</TableCell>
              <TableCell>RPN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFMs.map((fm) => (
              <TableRow
                key={fm.id}
                hover
                onClick={() => handleToggleFM(fm.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox">
                  <Checkbox checked={selectedFMIds.has(fm.id)} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={fm.severity}
                    size="small"
                    sx={{
                      bgcolor: getSeverityColor(fm.severity),
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 250 }}>
                  <Typography variant="body2" noWrap>
                    {fm.failureDescription}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap color="text.secondary">
                    {fm.failureCause || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{fm.rpnScore || '-'}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <RadioGroup
        value={targetMode}
        onChange={(e) =>
          setTargetMode(e.target.value as 'existing' | 'new')
        }
      >
        <FormControlLabel
          value="existing"
          control={<Radio />}
          label={t('drbfm.convert.existingSession')}
        />
        {targetMode === 'existing' && (
          <Box sx={{ pl: 4, pb: 2 }}>
            {sessionsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>{t('drbfm.convert.verificationSession')}</InputLabel>
                  <Select
                    value={selectedSessionId}
                    label={t('drbfm.convert.verificationSession')}
                    onChange={(e) => {
                      setSelectedSessionId(e.target.value);
                      setSelectedUnitId('');
                    }}
                  >
                    {existingSessions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.title}
                        {s.modelName ? ` (${s.modelName})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedSession && selectedSession.units.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('drbfm.convert.unit')}</InputLabel>
                    <Select
                      value={selectedUnitId}
                      label={t('drbfm.convert.unit')}
                      onChange={(e) =>
                        setSelectedUnitId(e.target.value)
                      }
                    >
                      <MenuItem value="">{t('drbfm.convert.createNewUnit')}</MenuItem>
                      {selectedSession.units.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.unitNumber} {u.unitName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </Box>
        )}

        <FormControlLabel
          value="new"
          control={<Radio />}
          label={t('drbfm.convert.newSession')}
        />
        {targetMode === 'new' && (
          <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              label={t('drbfm.convert.sessionTitle')}
              value={newSessionTitle}
              onChange={(e) => setNewSessionTitle(e.target.value)}
              required
              fullWidth
            />
            <TextField
              size="small"
              label={t('drbfm.convert.modelName')}
              value={newModelName}
              onChange={(e) => setNewModelName(e.target.value)}
              fullWidth
              placeholder={t('drbfm.convert.modelNamePlaceholder')}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label={t('drbfm.convert.unitNumber')}
                value={newUnitNumber}
                onChange={(e) => setNewUnitNumber(e.target.value)}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label={t('drbfm.convert.unitName')}
                value={newUnitName}
                onChange={(e) => setNewUnitName(e.target.value)}
                fullWidth
                placeholder={drbfmItemName}
              />
            </Box>
          </Box>
        )}
      </RadioGroup>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('drbfm.convert.previewDescription', { count: previewItems.length })}
      </Typography>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 350 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('drbfm.convert.itemName')}</TableCell>
              <TableCell>{t('drbfm.convert.classification')}</TableCell>
              <TableCell>{t('drbfm.convert.standardTarget')}</TableCell>
              <TableCell>{t('drbfm.convert.verificationMethod')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewItems.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap>
                    {item.itemName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {item.classifications.map((cls) => (
                      <Chip
                        key={cls}
                        label={getClassificationLabel(cls)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="body2" noWrap>
                    {item.standardTarget || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {item.methods.map((m) => (
                      <Chip
                        key={m}
                        label={getMethodLabel(m)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        {t('drbfm.convert.destination')}{' '}
        {targetMode === 'new'
          ? t('drbfm.convert.destinationNew', { title: newSessionTitle })
          : t('drbfm.convert.destinationExisting', {
              title: existingSessions.find((s) => s.id === selectedSessionId)?.title || '',
            })}
      </Alert>
    </Box>
  );

  const renderSuccess = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {t('drbfm.convert.successTitle', { count: resultItemsCount })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('drbfm.convert.successDescription')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button variant="outlined" onClick={onClose}>
          {t('common.close')}
        </Button>
        <Button variant="contained" onClick={handleNavigateToVerification}>
          {t('drbfm.convert.viewVerificationItems')}
        </Button>
      </Box>
    </Box>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: success ? 300 : 550 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ConvertIcon color="primary" />
          <Typography variant="h6">
            {t('drbfm.convert.dialogTitle', '検証項目に変換')}
          </Typography>
        </Box>
        {!isSubmitting && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          renderSuccess()
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {STEP_KEYS.map((stepKey) => (
                <Step key={stepKey}>
                  <StepLabel>{t(`drbfm.convert.steps.${stepKey}`)}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {activeStep === 0 && renderStep1()}
            {activeStep === 1 && renderStep2()}
            {activeStep === 2 && renderStep3()}
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={isSubmitting}>
              {t('drbfm.convert.back')}
            </Button>
          )}
          {activeStep < 2 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 ? !canProceedStep1 : !canProceedStep2}
            >
              {t('drbfm.convert.next')}
            </Button>
          )}
          {activeStep === 2 && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} />
                ) : (
                  <ConvertIcon />
                )
              }
            >
              {isSubmitting ? t('drbfm.convert.submitting') : t('drbfm.convert.submit')}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ConvertToVerificationDialog;
